import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth, hasPerm } from "./AuthContext";

const UnreadMessagesContext = createContext({ count: 0, refresh: () => {}, markAllRead: async () => {} });

// Fréquence de sondage du compteur global de messages non lus (badge affiché
// directement sur l'onglet Messagerie de la barre de navigation). Volontairement
// plus espacé que le sondage du fil de conversation ouvert (MessagesPage) :
// suffisant pour que le badge apparaisse "assez régulièrement" sans recharger
// toute la page, sans multiplier les appels réseau en arrière-plan permanent.
const POLL_INTERVAL_MS = 8000;

export function UnreadMessagesProvider({ children }) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const canSee = !!user && hasPerm(user, "communication");

  const refresh = useCallback(async () => {
    if (!canSee) return;
    try {
      const { data } = await api.get("/communication/messages/unread-count");
      setCount(data.count || 0);
    } catch {
      // silencieux : un échec ponctuel de sondage ne doit pas perturber l'affichage
    }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    refresh();
    timerRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [canSee, refresh]);

  const markAllRead = useCallback(async () => {
    await api.post("/communication/messages/mark-all-read");
    setCount(0);
  }, []);

  return (
    <UnreadMessagesContext.Provider value={{ count, refresh, markAllRead }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
}

export function useUnreadMessages() {
  return useContext(UnreadMessagesContext);
}
